import { useEffect, useCallback, useMemo, useState } from "react";
import { SUPPORT_CONFIG } from "@finitless/shared/supportConstants";

const bootPayload = (intercom) => ({
  app_id: intercom.appId,
  api_base: intercom.apiBase || "https://api-iam.intercom.io",
  hide_default_launcher: false,
});

/**
 * Custom hook for Intercom integration
 * Provides methods to interact with Intercom chat widget
 */
export const useIntercom = () => {
  const { intercom } = SUPPORT_CONFIG;

  const [isIntercomBooted, setIsIntercomBooted] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return typeof window.Intercom === "function";
  });

  // Initialize Intercom when component mounts
  useEffect(() => {
    if (!intercom.enabled || !intercom.appId) {
      console.warn("Intercom is disabled or app ID not provided");
      return;
    }

    const runBoot = () => {
      if (typeof window.Intercom === "function") {
        window.Intercom("boot", bootPayload(intercom));
        setIsIntercomBooted(true);
      }
    };

    // Load Intercom script if not already loaded
    if (!window.Intercom) {
      setIsIntercomBooted(false);
      const script = document.createElement("script");
      script.src = `https://widget.intercom.io/widget/${intercom.appId}`;
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        runBoot();
      };

      script.onerror = () => {
        console.warn("Failed to load Intercom script");
      };
    } else {
      runBoot();
    }

    // Cleanup on unmount
    return () => {
      setIsIntercomBooted(false);
      if (window.Intercom) {
        window.Intercom("shutdown");
      }
    };
  }, [intercom.appId, intercom.enabled, intercom.apiBase]);

  // Show Intercom messenger
  const showMessenger = useCallback(() => {
    if (window.Intercom) {
      window.Intercom("show");
    }
  }, []);

  // Hide Intercom messenger
  const hideMessenger = useCallback(() => {
    if (window.Intercom) {
      window.Intercom("hide");
    }
  }, []);

  // Show specific message in Intercom
  const showMessage = useCallback((message) => {
    if (window.Intercom) {
      window.Intercom("showNewMessage", message);
    }
  }, []);

  // Update user information in Intercom
  const updateUser = useCallback((userData) => {
    if (window.Intercom) {
      window.Intercom("update", userData);
    }
  }, []);

  // Track event in Intercom
  const trackEvent = useCallback((eventName, metadata = {}) => {
    if (window.Intercom) {
      window.Intercom("trackEvent", eventName, metadata);
    }
  }, []);

  // Shutdown Intercom completely
  const shutdown = useCallback(() => {
    if (window.Intercom) {
      window.Intercom("shutdown");
      setIsIntercomBooted(false);
    }
  }, []);

  const boot = useCallback(
    (userData = {}) => {
      if (window.Intercom) {
        window.Intercom("boot", {
          ...bootPayload(intercom),
          ...userData,
        });
        setIsIntercomBooted(true);
      }
    },
    [intercom],
  );

  return useMemo(
    () => ({
      showMessenger,
      hideMessenger,
      showMessage,
      updateUser,
      trackEvent,
      shutdown,
      boot,
      isEnabled: intercom.enabled && !!intercom.appId,
      /** True after widget script loaded and `boot` ran — IntercomContext waits on this to avoid dropped `update` calls. */
      isIntercomBooted,
    }),
    [
      showMessenger,
      hideMessenger,
      showMessage,
      updateUser,
      trackEvent,
      shutdown,
      intercom.enabled,
      intercom.appId,
      isIntercomBooted,
    ],
  );
};

export default useIntercom;
